import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { applyMaterialRules, newRow } from "./sheetModel";
import { normRange, inRange, cellText, coerce, toTSV, parseTSV, fillValues } from "./gridUtils";
import { GridContextMenu } from "./GridContextMenu";

const isRO = (col, row) => col.type === "computed" || (col.readOnly ? col.readOnly(row) : false);
const MOVE = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] };

// Excel-style grid: drag range select, fill handle, copy/paste (TSV), keyboard nav, context menu.
export const SheetGrid = ({ section, columns, rows, onRowsChange, onUndo, onRedo, selectedIds, onToggleRow, onToggleAll, supplierNames = {}, readOnly = false, normalizeRow = applyMaterialRules, createRow = newRow }) => {
  const [anchor, setAnchor] = useState(null);
  const [focus, setFocus] = useState(null);
  const [editing, setEditing] = useState(null);
  const [drag, setDrag] = useState(null);
  const [fillEnd, setFillEnd] = useState(null);
  const [menu, setMenu] = useState(null);
  const wrap = useRef(null);
  const clip = useRef("");
  const wantPicker = useRef(false);
  const rg = useMemo(() => (anchor && focus ? normRange(anchor, focus) : null), [anchor, focus]);
  const R = rows.length, C = columns.length;

  const commitRows = useCallback((next) => onRowsChange(next.map(r => normalizeRow(r))), [onRowsChange, normalizeRow]);
  const setCell = (r, c, text) => {
    const col = columns[c];
    if (isRO(col, rows[r])) return;
    const v = coerce(col, text, rows[r]);
    if (v === undefined) { toast.error(`"${text}" is not valid for ${col.label}`); return; }
    const next = rows.slice();
    const changed = { ...next[r], [col.key]: col.cast ? col.cast(v) : v };
    next[r] = col.transformRow ? col.transformRow(changed, v, rows[r]) : changed;
    commitRows(next);
  };
  const activate = (r, c, extend = false) => {
    const cell = { r: Math.max(0, Math.min(R - 1, r)), c: Math.max(0, Math.min(C - 1, c)) };
    setFocus(cell);
    if (!extend || !anchor) setAnchor(cell);
    setEditing(null);
  };
  const focusWrap = () => wrap.current?.focus({ preventScroll: true });

  useEffect(() => {
    const up = () => {
      if (drag === "fill" && rg && fillEnd !== null && fillEnd !== rg.r1 && fillEnd !== rg.r0) applyFill(fillEnd);
      setDrag(null); setFillEnd(null);
    };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!wantPicker.current || !focus) return;
    wantPicker.current = false;
    const el = wrap.current?.querySelector(`[data-cell="${focus.r}-${focus.c}"] select`);
    if (el) { try { el.focus(); el.showPicker?.(); } catch (_) { /* unsupported browser */ } }
  }, [focus]);

  const applyFill = (end) => {
    const down = end > rg.r1;
    const targets = down ? Array.from({ length: end - rg.r1 }, (_, i) => rg.r1 + 1 + i) : Array.from({ length: rg.r0 - end }, (_, i) => rg.r0 - 1 - i);
    const next = rows.slice();
    for (let c = rg.c0; c <= rg.c1; c++) {
      const col = columns[c];
      if (col.type === "computed") continue;
      const src = []; for (let r = rg.r0; r <= rg.r1; r++) src.push(rows[r][col.key]);
      const vals = fillValues(down ? src : src.slice().reverse(), targets.length);
      targets.forEach((r, i) => {
        if (isRO(col, next[r])) return;
        const changed = { ...next[r], [col.key]: vals[i] };
        next[r] = col.transformRow ? col.transformRow(changed, vals[i], next[r]) : changed;
      });
    }
    commitRows(next);
    setAnchor({ r: Math.min(rg.r0, end), c: rg.c0 }); setFocus({ r: Math.max(rg.r1, end), c: rg.c1 });
  };

  const copyRange = (e) => {
    if (!rg) return;
    const tsv = toTSV(rows, columns, rg);
    clip.current = tsv;
    if (e?.clipboardData) { e.clipboardData.setData("text/plain", tsv); e.preventDefault(); }
    else navigator.clipboard?.writeText(tsv).catch(() => {});
  };
  const clearRange = () => {
    if (!rg || readOnly) return;
    const next = rows.slice();
    for (let r = rg.r0; r <= rg.r1; r++) for (let c = rg.c0; c <= rg.c1; c++) {
      const col = columns[c];
      if (!isRO(col, next[r])) next[r] = { ...next[r], [col.key]: col.type === "number" ? 0 : col.type === "select" ? next[r][col.key] : "" };
    }
    commitRows(next);
  };
  const pasteText = (text) => {
    if (!rg || readOnly || !text) return;
    const matrix = parseTSV(text);
    const single = matrix.length === 1 && matrix[0].length === 1;
    const rowsN = single ? rg.r1 - rg.r0 + 1 : matrix.length, colsN = single ? rg.c1 - rg.c0 + 1 : Math.max(...matrix.map(l => l.length));
    const next = rows.slice();
    while (next.length < rg.r0 + rowsN) next.push(createRow(section));
    for (let i = 0; i < rowsN; i++) for (let j = 0; j < colsN; j++) {
      const c = rg.c0 + j; if (c >= C) continue;
      const col = columns[c]; const r = rg.r0 + i;
      if (isRO(col, next[r])) continue;
      const v = coerce(col, single ? matrix[0][0] : (matrix[i]?.[j] ?? ""), next[r]);
      if (v !== undefined) {
        const changed = { ...next[r], [col.key]: col.cast ? col.cast(v) : v };
        next[r] = col.transformRow ? col.transformRow(changed, v, next[r]) : changed;
      }
    }
    commitRows(next);
    setAnchor({ r: rg.r0, c: rg.c0 }); setFocus({ r: rg.r0 + rowsN - 1, c: Math.min(C - 1, rg.c0 + colsN - 1) });
  };
  const fillDown = () => { if (rg && rg.r1 > rg.r0) applyFillFrom({ ...rg, r1: rg.r0 }, rg.r1); };
  const applyFillFrom = (s, end) => {
    const next = rows.slice();
    for (let c = s.c0; c <= s.c1; c++) { const col = columns[c]; if (col.type === "computed") continue;
      for (let r = s.r0 + 1; r <= end; r++) if (!isRO(col, next[r])) {
        const value = rows[s.r0][col.key];
        const changed = { ...next[r], [col.key]: value };
        next[r] = col.transformRow ? col.transformRow(changed, value, next[r]) : changed;
      } }
    commitRows(next); setAnchor({ r: s.r0, c: s.c0 }); setFocus({ r: end, c: s.c1 });
  };
  const rowOp = (op) => {
    if (!rg || readOnly) return;
    let next = rows.slice();
    const n = rg.r1 - rg.r0 + 1;
    if (op === "insert_above") next.splice(rg.r0, 0, ...Array.from({ length: n }, () => createRow(section)));
    if (op === "insert_below") next.splice(rg.r1 + 1, 0, ...Array.from({ length: n }, () => createRow(section)));
    if (op === "duplicate") next.splice(rg.r1 + 1, 0, ...rows.slice(rg.r0, rg.r1 + 1).map(r => ({ ...createRow(section), ...r, row_id: createRow(section).row_id, dispatched_to: [] })));
    if (op === "delete") { next = next.filter((_, i) => i < rg.r0 || i > rg.r1); if (!next.length) next = [createRow(section)]; setAnchor(null); setFocus(null); }
    commitRows(next);
  };

  const onKeyDown = (e) => {
    if (editing) return;
    const meta = e.ctrlKey || e.metaKey;
    if (meta && e.key.toLowerCase() === "z") { e.preventDefault(); return e.shiftKey ? onRedo?.() : onUndo?.(); }
    if (meta && e.key.toLowerCase() === "y") { e.preventDefault(); return onRedo?.(); }
    if (meta && e.key.toLowerCase() === "a") { e.preventDefault(); setAnchor({ r: 0, c: 0 }); setFocus({ r: R - 1, c: C - 1 }); return; }
    if (meta && e.key.toLowerCase() === "d") { e.preventDefault(); return fillDown(); }
    if (!focus) return;
    const isSelect = e.target.tagName === "SELECT";
    if (MOVE[e.key]) { e.preventDefault(); const [dr, dc] = MOVE[e.key]; activate(focus.r + dr, focus.c + dc, e.shiftKey); focusWrap(); return; }
    if (e.key === "Tab") { e.preventDefault(); const c = focus.c + (e.shiftKey ? -1 : 1); if (c >= C) activate(focus.r + 1, 0); else if (c < 0) activate(focus.r - 1, C - 1); else activate(focus.r, c); focusWrap(); return; }
    if (e.key === "Enter" || e.key === "F2") {
      e.preventDefault();
      const col = columns[focus.c];
      if (readOnly || isRO(col, rows[focus.r])) { activate(focus.r + 1, focus.c); return; }
      if (col.type === "select") { if (isSelect) { activate(focus.r + 1, focus.c); focusWrap(); } else { wantPicker.current = true; setFocus({ ...focus }); } return; }
      setEditing({ r: focus.r, c: focus.c, value: cellText(col, rows[focus.r]) }); return;
    }
    if (e.key === "Escape") { setMenu(null); return; }
    if (isSelect) return;
    if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); return clearRange(); }
    if (!meta && e.key.length === 1 && !readOnly) {
      const col = columns[focus.c];
      if (isRO(col, rows[focus.r])) return;
    if (col.type === "select") { const v = coerce(col, e.key, rows[focus.r]); if (v !== undefined) setCell(focus.r, focus.c, e.key); return; }
      e.preventDefault(); setEditing({ r: focus.r, c: focus.c, value: e.key });
    }
  };

  const commitEdit = (move) => {
    if (!editing) return;
    setCell(editing.r, editing.c, editing.value);
    setEditing(null);
    if (move === "down") activate(editing.r + 1, editing.c); else if (move === "right") activate(editing.r, editing.c + 1);
    setTimeout(focusWrap, 0);
  };

  const cellDown = (e, r, c) => {
    if (e.button === 2) { if (!inRange(rg, r, c)) activate(r, c); return; }
    if (e.button !== 0) return;
    const col = columns[c];
    if (col.type === "select" && !isRO(col, rows[r]) && !readOnly) wantPicker.current = true;
    if (editing && (editing.r !== r || editing.c !== c)) commitEdit();
    if (e.target.tagName !== "INPUT" && e.target.tagName !== "SELECT") { e.preventDefault(); focusWrap(); }
    activate(r, c, e.shiftKey); setDrag("select");
  };

  const rangeCls = (r, c) => {
    const act = focus && focus.r === r && focus.c === c;
    const fillHit = drag === "fill" && rg && fillEnd !== null && c >= rg.c0 && c <= rg.c1 && ((fillEnd > rg.r1 && r > rg.r1 && r <= fillEnd) || (fillEnd < rg.r0 && r < rg.r0 && r >= fillEnd));
    return `${inRange(rg, r, c) ? "bg-indigo-50" : ""} ${act ? "outline outline-2 outline-indigo-600 -outline-offset-1 z-10" : ""} ${fillHit ? "bg-indigo-100/70 outline-dashed outline-1 outline-indigo-400" : ""}`;
  };

  const allSel = R > 0 && rows.every(r => selectedIds.has(r.row_id));
  return (
    <div className="relative">
      <div ref={wrap} tabIndex={0} onKeyDown={onKeyDown} onCopy={copyRange} onCut={(e) => { copyRange(e); clearRange(); }}
        onPaste={(e) => { e.preventDefault(); pasteText(e.clipboardData.getData("text/plain") || clip.current); }}
        onContextMenu={(e) => { e.preventDefault(); setMenu({ x: e.clientX, y: e.clientY }); }}
        className={`overflow-auto rounded-lg border bg-white outline-none focus:ring-2 focus:ring-indigo-300 ${drag ? "select-none cursor-crosshair" : ""}`}
        style={{ maxHeight: "62vh" }} data-testid={`sheet-grid-${section}`}>
        <table className="text-sm border-collapse" style={{ tableLayout: "fixed", width: 44 + 36 + columns.reduce((s, c) => s + c.width, 0) }}>
          <colgroup><col style={{ width: 36 }} /><col style={{ width: 44 }} />{columns.map(c => <col key={c.key} style={{ width: c.width }} />)}</colgroup>
          <thead className="sticky top-0 z-20 bg-slate-100 text-xs text-slate-600">
            <tr>
              <th className="border p-1"><input type="checkbox" checked={allSel} onChange={e => onToggleAll(e.target.checked)} data-testid={`select-all-${section}`} aria-label="Select all rows" /></th>
              <th className="border p-1 font-medium">#</th>
              {columns.map(c => <th key={c.key} className="border px-2 py-1.5 text-left font-semibold truncate">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={row.row_id} className="h-8">
                <td className="border text-center bg-slate-50"><input type="checkbox" checked={selectedIds.has(row.row_id)} onChange={e => onToggleRow(row.row_id, e.target.checked)} data-testid={`row-check-${section}-${r}`} aria-label={`Select row ${r + 1}`} /></td>
                <td className="border text-center text-xs text-slate-500 bg-slate-50 relative" title={row.dispatched_to?.length ? `Sent to: ${row.dispatched_to.map(id => supplierNames[id] || id).join(", ")}` : ""}>
                  {r + 1}{row.dispatched_to?.length > 0 && <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-emerald-500" data-testid={`dispatched-dot-${section}-${r}`} />}
                </td>
                {columns.map((col, c) => {
                  const ro = isRO(col, row) || readOnly;
                  const isEdit = editing && editing.r === r && editing.c === c;
                  const isAct = focus && focus.r === r && focus.c === c && rg && rg.r0 === rg.r1 && rg.c0 === rg.c1;
                  return (
                    <td key={col.key} data-cell={`${r}-${c}`} onMouseDown={e => cellDown(e, r, c)} onMouseEnter={() => { if (drag === "select") setFocus({ r, c }); if (drag === "fill") setFillEnd(r); }}
                      onDoubleClick={() => { if (!ro && col.type !== "select") setEditing({ r, c, value: cellText(col, row) }); }}
                      className={`border px-0 relative ${ro ? "bg-slate-50 text-slate-500" : ""} ${col.type === "number" || col.type === "measure" || col.type === "computed" ? "text-right" : ""} ${rangeCls(r, c)}`}
                      data-testid={`cell-${section}-${r}-${col.key}`}>
                      {isEdit ? (
                        <input autoFocus value={editing.value} onChange={e => setEditing({ ...editing, value: e.target.value })} onBlur={() => commitEdit()}
                          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); commitEdit("down"); } else if (e.key === "Tab") { e.preventDefault(); commitEdit("right"); } else if (e.key === "Escape") { setEditing(null); focusWrap(); } }}
                          className={`w-full h-8 px-2 outline-none bg-white ${col.type === "text" ? "" : "text-right font-mono"}`} data-testid={`cell-input-${section}-${r}-${col.key}`} />
                      ) : col.type === "select" && isAct && !ro ? (
                        <select value={row[col.key] ?? ""} onChange={e => setCell(r, c, e.target.value)} className="w-full h-8 px-1 bg-transparent outline-none text-sm" data-testid={`cell-select-${section}-${r}-${col.key}`}>
                          {(col.optionsFor ? col.optionsFor(row) : col.options).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      ) : (
                        <div className={`h-8 px-2 leading-8 truncate ${col.type === "measure" || col.type === "number" ? "font-mono" : ""}`}>{cellText(col, row) || <span className="text-slate-300">{col.type === "select" && !ro ? "▾" : ""}</span>}</div>
                      )}
                      {rg && !readOnly && r === rg.r1 && c === rg.c1 && !isEdit && (
                        <div onMouseDown={e => { e.stopPropagation(); e.preventDefault(); setDrag("fill"); setFillEnd(r); }} title="Drag to fill"
                          className="absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-indigo-600 border border-white cursor-crosshair z-30" data-testid={`fill-handle-${section}`} />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {R === 0 && <tr><td colSpan={C + 2} className="p-6 text-center text-slate-400 text-sm">No rows yet — add a row or paste from a spreadsheet.</td></tr>}
          </tbody>
        </table>
      </div>
      {menu && <GridContextMenu x={menu.x} y={menu.y} onClose={() => setMenu(null)} readOnly={readOnly} hasRange={!!rg} onAction={(a) => {
        setMenu(null);
        if (a === "copy") copyRange();
        else if (a === "cut") { copyRange(); clearRange(); }
        else if (a === "paste") navigator.clipboard?.readText().then(t => pasteText(t || clip.current)).catch(() => pasteText(clip.current));
        else if (a === "fill_down") fillDown();
        else if (a === "clear") clearRange();
        else rowOp(a);
        setTimeout(focusWrap, 0);
      }} />}
    </div>
  );
};
